import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  const servicesDropdown = [
    { path: '/services/maison', label: 'Débarras de maison' },
    { path: '/services/appartement', label: 'Débarras d\'appartement' },
    { path: '/services/cave-grenier', label: 'Débarras cave, grenier & garage' },
    { path: '/services/professionnel', label: 'Débarras professionnel' },
    { path: '/services/succession', label: 'Débarras succession' },
  ];

  const zonesDropdown = [
    { path: '/zones/annecy', label: 'Annecy et bassin Annécien' },
    { path: '/zones/mont-blanc', label: 'Pays du Mont Blanc - Chablais - Aravis' },
    { path: '/zones/aix-chambery', label: 'Aix-les-Bains - Chambéry - Maurienne - Tarentaise' },
    { path: '/zones/genevois', label: 'Genevois' },
    { path: '/zones/pays-arve', label: 'Vallée de l\'Arve' },
    { path: '/zones/pays-gex', label: 'Pays de Gex' },
  ];

  const navLinks = [
    { path: '/', label: 'Accueil' },
    { path: '/services', label: 'Services', dropdown: servicesDropdown },
    { path: '/zones', label: 'Zones d\'intervention', dropdown: zonesDropdown },
    { path: '/blog', label: 'Blog' },
    { path: '/faq', label: 'FAQ' },
    { path: '/a-propos', label: 'À propos' },
    { path: '/contact', label: 'Contact' }
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleDropdownToggle = (label) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
      <div className="container mx-auto px-4">
        {/* Top bar */}
        <div className="hidden md:flex items-center justify-end gap-6 py-2 text-sm border-b border-gray-100">
          <a href="tel:0451401617" className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors">
            <Phone className="w-4 h-4" />
            <span>04 51 40 16 17</span>
          </a>
          <a href="mailto:contact@debeco.fr" className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors">
            <Mail className="w-4 h-4" />
            <span>contact@debeco.fr</span>
          </a>
        </div>

        {/* Main header */}
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <picture>
              <source srcSet="/images/logo-header.webp" type="image/webp" />
              <img 
                src="/images/logo-header.png" 
                alt="Logo DEBECO, entreprise de débarras et recyclage en Savoie et Haute-Savoie" 
                className="h-16 w-auto group-hover:scale-105 transition-transform"
                width="213"
                height="64"
                decoding="async"
                fetchPriority="high"
                decoding="async"
                data-testid="header-logo"
              />
            </picture>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div key={link.path} className="relative group">
                {link.dropdown ? (
                  <div 
                    className="relative"
                    onMouseEnter={() => setOpenDropdown(link.label)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <Link
                      to={link.path}
                      className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-1 ${
                        isActive(link.path)
                          ? 'bg-blue-900 text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {link.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === link.label ? 'rotate-180' : ''}`} />
                    </Link>
                    
                    {/* Dropdown Menu */}
                    <div 
                      className={`absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[250px] transition-all duration-200 ${
                        openDropdown === link.label 
                          ? 'opacity-100 visible translate-y-0' 
                          : 'opacity-0 invisible -translate-y-2'
                      }`}
                    >
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`block px-4 py-2 text-sm transition-colors ${
                            location.pathname === item.path
                              ? 'bg-emerald-50 text-emerald-700 font-medium'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-emerald-600'
                          }`}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    to={link.path}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      isActive(link.path)
                        ? 'bg-blue-900 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {link.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Button Desktop */}
          <div className="hidden lg:block">
            <Link to="/devis">
              <Button className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg text-sm">
                Mon estimation gratuite
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden border-t border-gray-200 bg-white shadow-lg max-h-[80vh] overflow-y-auto">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
            {navLinks.map((link) => (
              <div key={link.path}>
                {link.dropdown ? (
                  <div>
                    <button
                      onClick={() => handleDropdownToggle(link.label)}
                      className={`w-full px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-between ${
                        isActive(link.path)
                          ? 'bg-blue-900 text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {link.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === link.label ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {openDropdown === link.label && (
                      <div className="ml-4 mt-2 space-y-1 border-l-2 border-emerald-500 pl-4">
                        <Link
                          to={link.path}
                          onClick={() => setIsMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:text-emerald-600 font-medium"
                        >
                          Voir tous
                        </Link>
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setIsMenuOpen(false)}
                            className={`block px-4 py-2 text-sm transition-colors ${
                              location.pathname === item.path
                                ? 'text-emerald-700 font-medium'
                                : 'text-gray-600 hover:text-emerald-600'
                            }`}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={link.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`block px-4 py-3 rounded-lg font-medium transition-all ${
                      isActive(link.path)
                        ? 'bg-blue-900 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {link.label}
                  </Link>
                )}
              </div>
            ))}
            <Link to="/devis" onClick={() => setIsMenuOpen(false)}>
              <Button className="w-full bg-gradient-to-r from-emerald-600 to-emerald-500 text-white mt-2">
                Mon estimation gratuite
              </Button>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
